import { useState } from "react";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Globe, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { CONTACT } from "@/lib/content";

const STEPS = [
  { n: "1", title: "Send us your requirement", desc: "Fill the form or email us directly — the more detail the better." },
  { n: "2", title: "Free scoping call", desc: "We'll set up a 30-minute call to understand your exact needs and constraints." },
  { n: "3", title: "Proposal & timeline", desc: "You receive a clear written proposal with scope, pricing, and delivery timeline." },
];

export default function Contact() {
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    firstName: "", lastName: "", email: "", org: "", interest: "", message: "",
  });

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));

  const onSubmit = (e) => {
    e.preventDefault();
    if (!form.firstName || !form.email || !form.message) {
      toast.error("Please fill in your name, email, and a message.");
      return;
    }
    setSubmitting(true);
    // No backend endpoint is wired up yet — swap this for a real API call
    // (e.g. api.contact(form)) once one exists.
    setTimeout(() => {
      setSubmitting(false);
      toast.success("Message sent! We'll be in touch within one business day.");
      setForm({ firstName: "", lastName: "", email: "", org: "", interest: "", message: "" });
    }, 900);
  };

  return (
    <div className="mx-auto max-w-7xl px-6 lg:px-14 pt-10 pb-24">
      <div className="max-w-2xl">
        <div className="overline text-cobalt-500">Contact</div>
        <h1 className="mt-2 font-display text-4xl sm:text-5xl font-black tracking-tighter">
          Let's start a conversation.
        </h1>
        <p className="mt-4 text-lg text-muted-foreground leading-relaxed">
          Whether you need Solution for your campus, a custom feature, or just have a question &mdash;
          we're here to help.
        </p>
      </div>

      <div className="mt-10 grid lg:grid-cols-[1fr_1.3fr] gap-8">
        {/* DIRECT CONTACT */}
        <div className="space-y-5">
          <div className="glass rounded-2xl p-6">
            <div className="overline text-cobalt-500 mb-4">Direct contact</div>
            <p className="text-sm text-muted-foreground mb-5">We respond within one business day.</p>
            <div className="space-y-4 text-sm">
              <a href={`tel:${CONTACT.phone.replace(/\s/g, "")}`} className="flex items-start gap-3 hover:text-cobalt-500">
                <Phone className="h-4 w-4 mt-0.5 flex-none text-cobalt-500" />
                <div>
                  <div className="text-xs text-muted-foreground">Phone</div>
                  <div className="font-medium">{CONTACT.phone}</div>
                </div>
              </a>
              <a href={`mailto:${CONTACT.email}`} className="flex items-start gap-3 hover:text-cobalt-500">
                <Mail className="h-4 w-4 mt-0.5 flex-none text-cobalt-500" />
                <div>
                  <div className="text-xs text-muted-foreground">Email</div>
                  <div className="font-medium">{CONTACT.email}</div>
                </div>
              </a>
              <div className="flex items-start gap-3">
                <MapPin className="h-4 w-4 mt-0.5 flex-none text-cobalt-500" />
                <div>
                  <div className="text-xs text-muted-foreground">Office</div>
                  <div className="font-medium leading-relaxed">{CONTACT.office}</div>
                </div>
              </div>
              <a href={`https://${CONTACT.website}`} target="_blank" rel="noreferrer" className="flex items-start gap-3 hover:text-cobalt-500">
                <Globe className="h-4 w-4 mt-0.5 flex-none text-cobalt-500" />
                <div>
                  <div className="text-xs text-muted-foreground">Website</div>
                  <div className="font-medium">{CONTACT.website}</div>
                </div>
              </a>
            </div>
          </div>

          <div className="glass rounded-2xl p-6">
            <div className="overline text-cobalt-500 mb-4">What to expect</div>
            <div className="space-y-4">
              {STEPS.map((s) => (
                <div key={s.n} className="flex items-start gap-3">
                  <div className="h-6 w-6 rounded-full bg-cobalt-500/10 text-cobalt-500 text-xs font-bold grid place-items-center flex-none mt-0.5">
                    {s.n}
                  </div>
                  <div>
                    <div className="text-sm font-medium">{s.title}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{s.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* FORM */}
        <form onSubmit={onSubmit} className="glass-strong rounded-3xl p-6 lg:p-8 space-y-5" data-testid="contact-form">
          <div>
            <div className="overline text-cobalt-500 mb-1">Send us a message</div>
            <p className="text-sm text-muted-foreground">We'll get back to you within one business day.</p>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="firstName">First name</Label>
              <Input id="firstName" value={form.firstName} onChange={update("firstName")} required />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="lastName">Last name</Label>
              <Input id="lastName" value={form.lastName} onChange={update("lastName")} />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">Work email</Label>
              <Input id="email" type="email" value={form.email} onChange={update("email")} required />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="org">Organisation</Label>
              <Input id="org" value={form.org} onChange={update("org")} />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="interest">What are you interested in?</Label>
            <Select value={form.interest} onValueChange={(v) => update("interest")(v)}>
              <SelectTrigger id="interest">
                <SelectValue placeholder="Select a service…" />
              </SelectTrigger>
              <SelectContent>
                {CONTACT.services.map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="message">Message</Label>
            <Textarea id="message" rows={5} value={form.message} onChange={update("message")} required />
          </div>

          <Button
            type="submit"
            disabled={submitting}
            size="lg"
            className="rounded-full font-medium h-12 px-6 w-full sm:w-auto bg-zinc-900 hover:bg-zinc-800 dark:bg-white dark:text-zinc-900 dark:hover:bg-zinc-100"
          >
            {submitting ? "Sending…" : "Send message"} <Send className="ml-1.5 h-4 w-4" />
          </Button>
        </form>
      </div>
    </div>
  );
}
